"use client";

import { Trans } from "@lingui/react/macro";
import { FragmentOf, readFragment } from "gql.tada";
import { DatabaseIcon, PlusIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  SidebarGroupAction,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { UA_POLICIES } from "@/lib/authorization/policies/user-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import CreateProjectDialog from "../../dialog/create-project-dialog";
import CollapsibleProjectGroup from "./collapsible-project-group";
import { UserAnalyticsProjectItemFragment } from "./fragments";
import SidebarMenuButtonWithPath from "./sidebar-menu-button-with-path";
import { SidebarSearchUnfilteredOnly } from "./sidebar-search-context";
import type { CollapsibleState } from "./types";
import UaSortableProjects from "./ua-sortable-projects";

type Props = {
  workspaceId: string;
  projectsData: ReadonlyArray<
    FragmentOf<typeof UserAnalyticsProjectItemFragment>
  >;
  initialGroupState: CollapsibleState;
  onboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
  completedOnboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
  workspaceUsedColors?: string[];
};

export default function UaSidebarGroup({
  workspaceId,
  projectsData,
  initialGroupState,
  onboardingSessions,
  completedOnboardingSessions,
  workspaceUsedColors,
}: Props) {
  const currentPathname = usePathname();
  const checkPolicy = useCheckPolicy();
  const validProjectsData = projectsData || [];

  // ビューデータのキーを生成（ビュー名の変更を検知するため）
  const dataKey = validProjectsData
    .map((data) => {
      const project = readFragment(UserAnalyticsProjectItemFragment, data);
      if (project?.userAnalytics?.views) {
        return project.userAnalytics.views
          .map((v) => `${v.id}:${v.name}`)
          .join(",");
      }
      return "";
    })
    .join("|");

  const canEditProjects = checkPolicy(UA_POLICIES["ua-project:edit"]).ok;
  const canEditBigQuery = checkPolicy(UA_POLICIES["ua-bigquery:edit"]).ok;

  return (
    <CollapsibleProjectGroup
      groupType="ua"
      title={<Trans>USER ANALYTICS</Trans>}
      initialState={initialGroupState}
      action={
        canEditProjects ? (
          <CreateProjectDialog
            workspaceId={workspaceId}
            type="ua"
            workspaceUsedColors={workspaceUsedColors}
            trigger={
              <SidebarGroupAction>
                <PlusIcon className="w-4 h-4" />
                <span className="sr-only">
                  <Trans>New Project</Trans>
                </span>
              </SidebarGroupAction>
            }
          />
        ) : undefined
      }
    >
      <SidebarMenu>
        <UaSortableProjects
          key={dataKey}
          workspaceId={workspaceId}
          projectsData={validProjectsData}
          currentPathname={currentPathname}
          onboardingSessions={onboardingSessions}
          completedOnboardingSessions={completedOnboardingSessions}
        />
        {canEditBigQuery ? (
          <SidebarSearchUnfilteredOnly>
            <SidebarMenuItem>
              <SidebarMenuButtonWithPath
                activePattern={`.*/${encodeURIComponent(
                  workspaceId,
                )}/settings/ua-bigquery`}
                asChild
              >
                <Link
                  href={`/${encodeURIComponent(workspaceId)}/settings/ua-bigquery`}
                >
                  <DatabaseIcon className="w-4 h-4" />
                  <Trans>User Analytics BigQuery</Trans>
                </Link>
              </SidebarMenuButtonWithPath>
            </SidebarMenuItem>
          </SidebarSearchUnfilteredOnly>
        ) : null}
      </SidebarMenu>
    </CollapsibleProjectGroup>
  );
}
